/* ==========================================================
   LIME LABS COMMERCE LAYER
   C-06C — THE ICONS / MÁS VENDIDOS DUAL CAROUSEL
   ----------------------------------------------------------
   Scope: desktop The Icons mega-menu only.

   - Preserves The Icons source: /collections/top-sellers
   - Adds existing Más vendidos: /collections/mas-vendidos
   - Uses the existing LLCL collections visual grammar
   - Adds horizontal product rails to both collection panels
   - Auto-scroll runs only while the active collection/panel
     is pointer-hovered
   - Keyboard focus activates panels without forced motion
   - V6.4 remains owner of top-level mega-menu state
   - Mobile drawer is untouched
   ========================================================== */

(() => {
  'use strict';

  const desktop = window.matchMedia('(min-width: 700px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const HEADER = '.ll-header--llcl';
  const BLOCK_SELECTOR = `${HEADER} [data-ll-mega-block][data-ll-menu-key="the-icons"]`;
  const V64 = '__LLCL_MEGA_MENU_V64__';
  const VERSION = 'c06c-icons-dual-v2-locale';
  const routeRoot = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const locale = String(document.documentElement.lang || 'es').trim().toLowerCase().split('-')[0];
  const language = locale === 'en' ? 'en' : 'es';
  const COPY = {
    es: {
      product: 'Producto',
      soldOut: 'Agotado',
      bestSellers: 'Más Vendidos',
      selectionEyebrow: 'LA TROUPE / SELECCIÓN',
      bestSellersEyebrow: 'LA TROUPE / MÁS VENDIDOS',
      viewTheIcons: 'Ver The Icons',
      viewBestSellers: 'Ver Más Vendidos',
      iconsChrome: 'THE ICONS / ARCHIVO',
      bestSellersChrome: 'MÁS VENDIDOS / ARCHIVO',
      viewCollection: 'VER COLECCIÓN',
      productsOf: 'Productos de',
      worldLabel: 'The Icons y Más Vendidos'
    },
    en: {
      product: 'Product',
      soldOut: 'Sold out',
      bestSellers: 'Best Sellers',
      selectionEyebrow: 'LA TROUPE / SELECTION',
      bestSellersEyebrow: 'LA TROUPE / BEST SELLERS',
      viewTheIcons: 'View The Icons',
      viewBestSellers: 'View Best Sellers',
      iconsChrome: 'THE ICONS / ARCHIVE',
      bestSellersChrome: 'BEST SELLERS / ARCHIVE',
      viewCollection: 'VIEW COLLECTION',
      productsOf: 'Products from',
      worldLabel: 'The Icons and Best Sellers'
    }
  };
  const t = (key) => COPY[language]?.[key] ?? COPY.es[key] ?? key;

  let motionControllers = [];
  let scheduleToken = 0;

  const entries = [
    {
      key: 'the-icons',
      title: 'The Icons',
      sourceHandle: 'top-sellers',
      href: `${routeRoot}collections/top-sellers`,
      eyebrow: t('selectionEyebrow'),
      cta: t('viewTheIcons'),
      chrome: t('iconsChrome')
    },
    {
      key: 'mas-vendidos',
      title: t('bestSellers'),
      sourceHandle: 'mas-vendidos',
      href: `${routeRoot}collections/mas-vendidos`,
      eyebrow: t('bestSellersEyebrow'),
      cta: t('viewBestSellers'),
      chrome: t('bestSellersChrome')
    }
  ];

  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  const storePath = (path) => {
    const clean = String(path || '').replace(/^\//, '');
    return `${routeRoot}${clean}`;
  };

  const imageUrl = (product) => {
    const featured = product?.featured_image;
    if (typeof featured === 'string') return featured;
    if (featured?.src) return featured.src;

    const first = product?.images?.[0];
    if (typeof first === 'string') return first;
    return first?.src || '';
  };

  const imageAlt = (product) => {
    const first = product?.images?.[0];
    if (typeof first === 'object' && first?.alt) return first.alt;
    return product?.title || '';
  };

  const productHref = (product) =>
    storePath(`products/${encodeURIComponent(product.handle)}`);

  const fetchProducts = async (handle, limit = 12) => {
    const url = `${storePath(`collections/${encodeURIComponent(handle)}/products.json`)}?limit=${limit}`;
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Collection ${handle} returned ${response.status}`);
    }

    const payload = await response.json();
    return (payload?.products || []).filter(
      (product) => product?.handle && imageUrl(product)
    );
  };

  const arrowIcon = (className = '') => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    if (className) svg.setAttribute('class', className);
    svg.setAttribute('viewBox', '0 0 18 10');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M1 5H16M12 1L16 5L12 9');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    svg.appendChild(path);

    return svg;
  };

  const buildProductCard = (product, index) => {
    const article = make(
      'article',
      `ll-mega-editorial-product ll-c06c-icons-carousel__card ll-mega-editorial-product--${index}`
    );
    article.dataset.llEditorialProduct = '';
    article.dataset.productIndex = String(index);

    if (product?.available === false) article.classList.add('is-unavailable');

    const link = make('a', 'll-mega-editorial-product__link');
    link.href = productHref(product);
    link.setAttribute('aria-label', product.title || t('product'));

    const media = make('div', 'll-mega-editorial-product__media');
    const image = make('img', 'll-mega-editorial-product__image');
    image.src = imageUrl(product);
    image.alt = imageAlt(product);
    image.loading = 'lazy';
    image.decoding = 'async';
    media.appendChild(image);
    media.appendChild(make('span', 'll-mega-editorial-product__edge'));

    const mediaIndex = make(
      'span',
      'll-mega-editorial-product__media-index',
      String(index).padStart(2, '0')
    );
    mediaIndex.setAttribute('aria-hidden', 'true');
    media.appendChild(mediaIndex);

    if (product?.available === false) {
      media.appendChild(make('span', 'll-mega-editorial-product__availability', t('soldOut')));
    }

    const caption = make('div', 'll-mega-editorial-product__caption');
    const captionIndex = make(
      'span',
      'll-mega-editorial-product__index',
      String(index).padStart(2, '0')
    );
    captionIndex.setAttribute('aria-hidden', 'true');
    caption.appendChild(captionIndex);
    caption.appendChild(make('span', 'll-mega-editorial-product__title', product.title || ''));

    link.append(media, caption);
    article.appendChild(link);
    return article;
  };

  const createHoverAutoscroll = (track) => {
    let timer = null;

    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    const tick = () => {
      if (!desktop.matches || reducedMotion.matches || document.hidden) return;
      if (!track.isConnected || track.scrollWidth <= track.clientWidth + 4) return;

      const firstCard = track.querySelector('.ll-c06c-icons-carousel__card');
      const style = window.getComputedStyle(track);
      const gap = Number.parseFloat(style.columnGap || style.gap) || 0;
      const step = firstCard ? firstCard.getBoundingClientRect().width + gap : 220;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - step * 0.6;

      track.scrollTo({
        left: atEnd ? 0 : track.scrollLeft + step,
        behavior: 'smooth'
      });
    };

    const start = () => {
      if (reducedMotion.matches || timer) return;
      timer = window.setInterval(tick, 2100);
    };

    return { start, stop };
  };

  const stopAllMotion = () => {
    motionControllers.forEach((controller) => controller.stop());
    motionControllers = [];
  };

  const buildPanel = async (entry, panel, panelIndex) => {
    const wrapper = make(
      'div',
      'll-mega-editorial-collection ll-mega-editorial-collection--collections ll-c06c-icons-carousel'
    );
    wrapper.dataset.llEditorialCollection = '';
    wrapper.dataset.collectionHandle = entry.sourceHandle;

    const header = make('header', 'll-mega-editorial-collection__header');
    const identity = make('div', 'll-mega-editorial-collection__identity');
    identity.appendChild(make('span', 'll-mega-editorial-collection__eyebrow', entry.eyebrow));

    const rule = make('span', 'll-mega-editorial-collection__identity-rule');
    rule.setAttribute('aria-hidden', 'true');
    identity.appendChild(rule);
    identity.appendChild(make('span', 'll-mega-editorial-collection__name', entry.title));

    const view = make('a', 'll-mega-editorial-collection__view');
    view.href = entry.href;
    view.appendChild(make('span', '', entry.cta));
    view.appendChild(arrowIcon('ll-mega-editorial-collection__view-icon'));
    header.append(identity, view);
    wrapper.appendChild(header);

    try {
      const products = await fetchProducts(entry.sourceHandle, 12);
      const selected = products.slice(0, 8);

      if (selected.length) {
        const viewport = make('div', 'll-c06c-icons-carousel__viewport');
        const track = make(
          'div',
          'll-mega-editorial-collection__products ll-c06c-icons-carousel__track'
        );
        track.dataset.llEditorialProducts = '';
        track.setAttribute('aria-label', `${t('productsOf')} ${entry.title}`);

        selected.forEach((product, index) => {
          track.appendChild(buildProductCard(product, index + 1));
        });

        viewport.appendChild(track);
        wrapper.appendChild(viewport);

        const motion = createHoverAutoscroll(track);
        motionControllers.push(motion);
        panel.__llIconsMotion = motion;
      } else {
        const empty = make(
          'a',
          'll-mega-editorial-collection__empty ll-c06c-icons-carousel__fallback',
          t('viewCollection')
        );
        empty.href = entry.href;
        wrapper.appendChild(empty);
      }
    } catch (_error) {
      const empty = make(
        'a',
        'll-mega-editorial-collection__empty ll-c06c-icons-carousel__fallback',
        t('viewCollection')
      );
      empty.href = entry.href;
      wrapper.appendChild(empty);
    }

    const chrome = make('div', 'll-mega-editorial-collection__chrome');
    chrome.setAttribute('aria-hidden', 'true');
    chrome.appendChild(make('span', '', 'LA TROUPE'));
    chrome.appendChild(make('span', 'll-mega-editorial-collection__chrome-rule'));
    chrome.appendChild(make('span', '', entry.chrome));
    chrome.appendChild(make('span', '', `${String(panelIndex + 1).padStart(2, '0')} / 02`));
    wrapper.appendChild(chrome);

    panel.replaceChildren(wrapper);
  };

  const activatePanel = (shell, panelId, autoplay = false) => {
    const links = Array.from(shell.querySelectorAll('[data-ll-mega-trigger]'));
    const panels = Array.from(shell.querySelectorAll('[data-ll-mega-panel]'));

    motionControllers.forEach((controller) => controller.stop());

    links.forEach((link) => {
      const active = link.dataset.llMegaTrigger === panelId;
      link.classList.toggle('is-media-active', active);
      link.dataset.llMediaActive = active ? 'true' : 'false';
      link.setAttribute('aria-current', active ? 'true' : 'false');
    });

    panels.forEach((panel) => {
      const active = panel.dataset.llMegaPanel === panelId;
      panel.classList.toggle('is-active', active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');

      if (active && autoplay) panel.__llIconsMotion?.start?.();
    });

    shell.dataset.llActivePanel = panelId;
  };

  const bindPanelInteractions = (shell) => {
    const links = Array.from(shell.querySelectorAll('[data-ll-mega-trigger]'));
    const panels = Array.from(shell.querySelectorAll('[data-ll-mega-panel]'));

    links.forEach((link) => {
      const panelId = link.dataset.llMegaTrigger;

      link.addEventListener('pointerenter', (event) => {
        if (!desktop.matches || event.pointerType === 'touch') return;
        activatePanel(shell, panelId, true);
      });

      link.addEventListener('focusin', () => {
        if (!desktop.matches) return;
        activatePanel(shell, panelId, false);
      });
    });

    panels.forEach((panel) => {
      panel.addEventListener('pointerenter', (event) => {
        if (!desktop.matches || event.pointerType === 'touch') return;
        activatePanel(shell, panel.dataset.llMegaPanel, true);
      });
    });

    shell.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch') return;
      motionControllers.forEach((controller) => controller.stop());
    });
  };

  const hasCurrentWorld = (shell) => {
    if (!shell || shell.dataset.llIconsDualVersion !== VERSION) return false;

    const links = shell.querySelectorAll('[data-ll-mega-trigger]');
    const tracks = shell.querySelectorAll('.ll-c06c-icons-carousel__track');
    return links.length === 2 && tracks.length === 2;
  };

  const buildWorld = async () => {
    if (!desktop.matches) return;

    const shell = document.querySelector(BLOCK_SELECTOR);
    if (!shell || hasCurrentWorld(shell)) return;

    stopAllMotion();

    shell.classList.remove(
      'll-mega-menu--commerce',
      'll-mega-menu--editorial',
      'll-mega-menu--journal',
      'll-mega-menu--boutiques',
      'll-mega-menu--media-left',
      'll-mega-menu--c06c',
      'll-c06c-home',
      'll-c06c-unisex',
      'll-c06c-editions',
      'll-c06c-icons'
    );

    shell.classList.add(
      'll-mega-menu',
      'll-mega-menu--dynamic',
      'll-mega-menu--collections',
      'll-mega-menu--media-right',
      'll-c06c-native-world',
      'll-c06c-icons-native',
      'll-c06c-icons-dual'
    );

    shell.dataset.llMegaMode = 'collections';
    shell.dataset.llC06cHydrated = 'true';
    shell.dataset.llIconsDualVersion = VERSION;
    shell.removeAttribute('aria-busy');
    shell.replaceChildren();

    const nav = make(
      'nav',
      'll-mega-menu__nav ll-mega-menu__nav--editorial ll-mega-menu__nav--collections'
    );
    nav.setAttribute('aria-label', 'The Icons');

    const list = make('ul', 'll-mega-menu__editorial-list unstyled-list');
    list.setAttribute('role', 'list');
    nav.appendChild(list);

    const aside = make(
      'aside',
      'll-mega-menu__editorial ll-mega-menu__editorial--universal'
    );
    aside.dataset.llMediaStage = '';
    aside.setAttribute('aria-label', t('worldLabel'));

    const stage = make('div', 'll-mega-menu__collection-stage');
    aside.appendChild(stage);

    const jobs = [];

    entries.forEach((entry, index) => {
      const panelId = `ll-c06c-the-icons-${entry.key}`;

      const item = make('li', 'll-mega-menu__editorial-item');
      const link = make(
        'a',
        'll-mega-menu__heading ll-mega-menu__heading--editorial',
        entry.title
      );
      link.href = entry.href;
      link.dataset.llItemKey = entry.key;
      link.dataset.llParentKey = '';
      link.dataset.llMegaTrigger = panelId;
      if (index === 0) {
        link.classList.add('is-media-active');
        link.dataset.llMediaActive = 'true';
        link.setAttribute('aria-current', 'true');
      }
      item.appendChild(link);
      list.appendChild(item);

      const panel = make(
        'section',
        `ll-mega-hover-panel ll-mega-hover-panel--editorial-collection${index === 0 ? ' is-active' : ''}`
      );
      panel.dataset.llMegaPanel = panelId;
      panel.dataset.llAutoMedia = '';
      panel.dataset.llItemKey = entry.key;
      panel.dataset.llParentKey = '';
      panel.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      stage.appendChild(panel);

      jobs.push(buildPanel(entry, panel, index));

      if (index === 0) {
        shell.dataset.llMegaDefaultPanel = panelId;
        shell.dataset.llActivePanel = panelId;
      }
    });

    shell.append(nav, aside);
    await Promise.allSettled(jobs);

    bindPanelInteractions(shell);
    window[V64]?.refresh?.();
  };

  const scheduleBuild = () => {
    const token = ++scheduleToken;
    [0, 180, 650].forEach((delay) => {
      window.setTimeout(() => {
        if (token !== scheduleToken) return;
        buildWorld();
      }, delay);
    });
  };

  const boot = () => scheduleBuild();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', scheduleBuild);

  const onDesktopChange = () => {
    stopAllMotion();
    scheduleBuild();
  };

  if (typeof desktop.addEventListener === 'function') {
    desktop.addEventListener('change', onDesktopChange);
  } else if (typeof desktop.addListener === 'function') {
    desktop.addListener(onDesktopChange);
  }
})();
